import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import ScrollReveal from '@/components/base/ScrollReveal';
import JsonLd from '@/components/base/JsonLd';
import { useTranslation } from 'react-i18next';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Génie Civil & BTP — IS TELECOM',
  description: 'Construction métallique, fondations spéciales, charpentes et bâtiments industriels en Afrique. Expertise en infrastructures de télécommunication, industrielles et ouvrages d\'art.',
  provider: {
    '@type': 'Organization',
    name: 'IS TELECOM',
    url: 'https://is-telecom.co',
  },
  areaServed: 'Afrique de l\'Ouest et de l\'Est (Bénin, Togo, Mali, Zambie, Tanzanie, Sénégal)',
};

export default function GenieCivilBTP() {
  const { t } = useTranslation();

  const services = [
    {
      title: 'Construction Métallique',
      desc: 'Conception, fabrication et montage de structures métalliques lourdes pour pylônes de télécommunication, passerelles, hangars et ossatures industrielles. Acier galvanisé haute résistance, assemblages boulonnés et soudés contrôlés par nos équipes certifiées.',
      icon: 'ri-building-2-line',
      image: 'https://readdy.ai/api/search-image?query=Black%20African%20construction%20workers%20with%20dark%20skin%20assembling%20large%20steel%20structure%20framework%20on%20construction%20site%20in%20Africa%2C%20professional%20dark-skinned%20metal%20workers%20welding%20and%20bolting%20industrial%20steel%20beams%2C%20safety%20helmets%20and%20orange%20vests%2C%20bright%20daylight%20industrial%20construction%20photography%20with%20cranes%20and%20blue%20sky&width=800&height=500&seq=btp-metal-01&orientation=landscape',
    },
    {
      title: 'Fondations & Terrassement',
      desc: 'Études géotechniques, fondations profondes sur pieux, semelles isolées et filantes, radiers en béton armé. Nous réalisons les fondations de pylônes de télécommunication, bâtiments industriels et ouvrages d\'art selon les normes Eurocodes et BAEL.',
      icon: 'ri-landscape-line',
      image: 'https://readdy.ai/api/search-image?query=Black%20African%20civil%20engineering%20workers%20with%20dark%20skin%20pouring%20concrete%20for%20deep%20foundation%20of%20telecommunications%20tower%20in%20Africa%2C%20professional%20dark-skinned%20construction%20team%20working%20on%20reinforced%20concrete%20pile%20foundation%20with%20rebar%20cage%20and%20formwork%2C%20bright%20daylight%20construction%20site%20photography%20with%20excavation%20and%20heavy%20equipment&width=800&height=500&seq=btp-foundation-01&orientation=landscape',
    },
    {
      title: 'Charpentes & Couvertures',
      desc: 'Charpentes métalliques et mixtes pour bâtiments industriels, hangars logistiques, entrepôts et centres de données. Couverture en bac acier, panneaux sandwich et étanchéité multicouche. Calcul de structures assisté par logiciels aux éléments finis.',
      icon: 'ri-home-gear-line',
      image: 'https://readdy.ai/api/search-image?query=Black%20African%20steel%20structure%20workers%20with%20dark%20skin%20installing%20large%20metal%20roof%20trusses%20and%20framework%20on%20industrial%20warehouse%20building%20in%20Africa%2C%20professional%20dark-skinned%20crew%20assembling%20steel%20roof%20structure%2C%20safety%20equipment%20and%20harnesses%2C%20bright%20sky%20background%20with%20industrial%20construction%20site&width=800&height=500&seq=btp-roofing-01&orientation=landscape',
    },
    {
      title: 'Bâtiments Industriels',
      desc: 'Construction clé en main de bâtiments industriels : centrales électriques, data centers, ateliers de maintenance, entrepôts logistiques. Maîtrise d\'œuvre complète du terrassement aux finitions, en passant par les lots techniques (électricité, climatisation, sécurité incendie).',
      icon: 'ri-building-line',
      image: 'https://readdy.ai/api/search-image?query=Black%20African%20construction%20team%20with%20dark%20skin%20building%20modern%20industrial%20facility%20in%20Africa%2C%20professional%20dark-skinned%20workers%20constructing%20large%20industrial%20warehouse%20with%20steel%20frame%20and%20concrete%20walls%2C%20construction%20site%20with%20scaffolding%20and%20cranes%2C%20bright%20daylight%20with%20progress%20visible&width=800&height=500&seq=btp-industrial-01&orientation=landscape',
    },
  ];

  const stats = [
    { value: '2000', suffix: '+', label: 'Fondations réalisées' },
    { value: '500', suffix: '+', label: 'Bâtiments construits' },
    { value: '6', suffix: '', label: 'Pays d\'intervention' },
    { value: '15000', suffix: ' t', label: 'Acier déployé' },
  ];

  return (
    <Layout>
      <JsonLd data={schema} />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-background-950">
        <div className="absolute inset-0 opacity-35">
          <img
            src="https://readdy.ai/api/search-image?query=Black%20African%20construction%20workers%20with%20dark%20skin%20building%20massive%20industrial%20steel%20structure%20at%20golden%20hour%2C%20professional%20dark-skinned%20BTP%20civil%20engineering%20team%20on%20large%20construction%20site%20in%20Africa%2C%20dramatic%20cinematic%20lighting%20with%20cranes%20silhouettes%2C%20heavy%20construction%20and%20steel%20framework%2C%20warm%20sunset%20tones%20over%20African%20landscape&width=1920&height=900&seq=btp-hero-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/90 via-background-950/70 to-background-950/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
          <div className="inline-flex items-center gap-2 border border-secondary-400/50 rounded-full px-5 py-2.5 mb-8">
            <i className="ri-building-2-line text-secondary-400 text-sm w-4 h-4 flex items-center justify-center" />
            <span className="text-secondary-300 text-sm font-medium uppercase tracking-wider">Génie Civil & BTP</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold font-heading text-white mb-6 leading-[1.1]">
            Génie Civil<br />
            <span className="text-secondary-400">&amp; Construction</span>
          </h1>
          <p className="text-lg md:text-xl text-background-200 max-w-2xl mx-auto leading-relaxed mb-10">
            Des fondations de vos pylônes de télécommunication à la construction de vos bâtiments industriels,
            IS TELECOM déploie son expertise BTP dans 6 pays d'Afrique.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis" className="inline-flex items-center gap-2 bg-secondary-500 hover:bg-secondary-600 text-background-950 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Demander un devis
              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-transparent border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-background-900 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-4xl md:text-5xl font-bold font-heading text-secondary-400 mb-1">
                  {s.value}<span className="text-2xl">{s.suffix}</span>
                </div>
                <div className="text-background-300 text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <ScrollReveal variant="fade-up">
        <section className="py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
                <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">Nos Expertises BTP</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 max-w-3xl mx-auto leading-tight">
                Du sol au ciel,<br />nous construisons l&apos;Afrique
              </h2>
              <p className="text-foreground-500 mt-4 max-w-2xl mx-auto">
                Terrassement, fondations, charpentes métalliques, bâtiments industriels : notre maîtrise du gros œuvre garantit des infrastructures durables pour la télécommunication et l&apos;industrie.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map((svc) => (
                <div key={svc.title} className="group bg-white rounded-3xl overflow-hidden border border-background-200/70 hover:shadow-xl transition-all duration-500">
                  <div className="h-56 overflow-hidden">
                    <img src={svc.image} alt={svc.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center">
                        <i className={`${svc.icon} text-secondary-600 text-xl w-6 h-6 flex items-center justify-center`} />
                      </div>
                      <h3 className="text-xl font-bold font-heading text-foreground-950">{svc.title}</h3>
                    </div>
                    <p className="text-foreground-500 text-sm leading-relaxed">{svc.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Normes & Certifications */}
      <ScrollReveal variant="fade-up" delay={100}>
        <section className="py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
                  <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">Normes & Sécurité</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 mb-6 leading-tight">
                  Des ouvrages conformes<br />aux standards internationaux
                </h2>
                <p className="text-foreground-600 text-lg leading-relaxed mb-8">
                  Tous nos ouvrages de génie civil sont conçus selon les normes internationales les plus strictes.
                  Bureau d&apos;études intégré, logiciels de calcul aux éléments finis et contrôles qualité rigoureux à chaque étape.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: 'ri-shield-check-line', title: 'Eurocodes 0 à 8', desc: 'Conception et calcul des structures selon normes européennes' },
                    { icon: 'ri-earth-line', title: 'BAEL / Eurocode 2', desc: 'Calcul des ouvrages en béton armé' },
                    { icon: 'ri-test-tube-line', title: 'Études Géotechniques', desc: 'Sondages, essais pressiométriques et analyses de sol (normes NF P94)' },
                    { icon: 'ri-award-line', title: 'ISO 9001 & 14001', desc: 'Management qualité et environnemental certifiés' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-secondary-100 rounded-xl flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-secondary-600 text-lg w-5 h-5 flex items-center justify-center`} />
                      </div>
                      <div>
                        <div className="font-semibold text-foreground-950 text-sm">{item.title}</div>
                        <div className="text-foreground-500 text-xs">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-3xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Black%20African%20civil%20engineer%20with%20dark%20skin%20reviewing%20construction%20blueprints%20and%20technical%20drawings%20on%20BTP%20construction%20site%20in%20Africa%2C%20professional%20dark-skinned%20engineer%20wearing%20white%20helmet%20and%20safety%20vest%2C%20holding%20rolled%20architectural%20plans%2C%20industrial%20construction%20background%20with%20steel%20framework%20and%20concrete%20structures%2C%20professional%20engineering%20photography%20with%20natural%20lighting&width=800&height=600&seq=btp-engineer-01&orientation=landscape"
                  alt="Ingénieur BTP sur chantier"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA */}
      <section className="py-24 bg-background-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-white mb-4">
            Un projet de construction ?
          </h2>
          <p className="text-lg text-background-300 max-w-3xl mx-auto mb-8">
            Fondations, charpentes métalliques ou bâtiment industriel clé en main — notre bureau d&apos;études vous accompagne de la conception à la livraison.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis" className="inline-flex items-center gap-2 bg-secondary-500 hover:bg-secondary-600 text-background-950 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Demander un devis
              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-transparent border-2 border-secondary-400 text-secondary-400 hover:bg-secondary-500/10 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}